import { useEffect, useState } from "react";
import { Save, Loader2, Plus, X, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export function SettingsView() {
  const { t } = useLang();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  if (!s) return <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const setDay = (d, k, v) => setS((prev) => ({ ...prev, opening_hours: { ...prev.opening_hours, [d]: { ...prev.opening_hours[d], [k]: v } } }));
  const addBlocked = () => { if (newDate && !s.blocked_dates.includes(newDate)) { setS((p) => ({ ...p, blocked_dates: [...p.blocked_dates, newDate].sort() })); setNewDate(""); } };
  const rmBlocked = (d) => setS((p) => ({ ...p, blocked_dates: p.blocked_dates.filter((x) => x !== d) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        opening_hours: s.opening_hours,
        slot_minutes: Number(s.slot_minutes) || 30,
        lunch_start: s.lunch_start || null,
        lunch_end: s.lunch_end || null,
        blocked_dates: s.blocked_dates,
      });
      toast.success(t("admin.set_saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="settings-view">
      <h3 className="font-display font-700 text-white text-lg tracking-tight mb-5 flex items-center gap-2"><Clock className="w-4 h-4 text-[#E60000]" /> {t("admin.set_title")}</h3>

      <div className="space-y-px bg-white/10 border border-white/10">
        {DAYS.map((d) => {
          const day = s.opening_hours[d];
          return (
            <div key={d} className="bg-[#0C0C0E] p-4 flex flex-wrap items-center gap-4" data-testid={`settings-day-${d}`}>
              <span className="font-display font-700 text-white w-24">{t(`admin.days.${d}`)}</span>
              <label className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-wider text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={!day.closed} onChange={(e) => setDay(d, "closed", !e.target.checked)} data-testid={`settings-open-${d}`} className="accent-[#E60000] w-4 h-4" />
                {day.closed ? t("admin.set_closed") : t("admin.set_open")}
              </label>
              {!day.closed && (
                <div className="flex items-center gap-2 ml-auto">
                  <Input type="time" value={day.open} onChange={(e) => setDay(d, "open", e.target.value)} data-testid={`settings-from-${d}`} className="bg-[#050505] border-white/10 rounded-none h-10 w-32 text-white text-sm focus-visible:border-[#00E5FF] focus-visible:ring-0" />
                  <span className="text-zinc-600">→</span>
                  <Input type="time" value={day.close} onChange={(e) => setDay(d, "close", e.target.value)} data-testid={`settings-to-${d}`} className="bg-[#050505] border-white/10 rounded-none h-10 w-32 text-white text-sm focus-visible:border-[#00E5FF] focus-visible:ring-0" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mt-6">
        <div>
          <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.set_slot")}</label>
          <Input type="number" value={s.slot_minutes} onChange={(e) => setS((p) => ({ ...p, slot_minutes: e.target.value }))} data-testid="settings-slot" className="bg-[#0C0C0E] border-white/10 rounded-none h-11 text-white focus-visible:border-[#00E5FF] focus-visible:ring-0" />
        </div>
        <div>
          <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.set_lunch")} — {t("admin.set_lunch_start")}</label>
          <Input type="time" value={s.lunch_start || ""} onChange={(e) => setS((p) => ({ ...p, lunch_start: e.target.value }))} data-testid="settings-lunch-start" className="bg-[#0C0C0E] border-white/10 rounded-none h-11 text-white focus-visible:border-[#00E5FF] focus-visible:ring-0" />
        </div>
        <div>
          <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.set_lunch_end")}</label>
          <Input type="time" value={s.lunch_end || ""} onChange={(e) => setS((p) => ({ ...p, lunch_end: e.target.value }))} data-testid="settings-lunch-end" className="bg-[#0C0C0E] border-white/10 rounded-none h-11 text-white focus-visible:border-[#00E5FF] focus-visible:ring-0" />
        </div>
      </div>

      <div className="mt-6">
        <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.set_blocked")}</label>
        <div className="flex items-center gap-2">
          <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="settings-blocked-date" className="bg-[#0C0C0E] border-white/10 rounded-none h-11 w-52 text-white focus-visible:border-[#00E5FF] focus-visible:ring-0" />
          <button onClick={addBlocked} data-testid="settings-add-blocked" className="inline-flex items-center gap-1.5 px-4 h-11 border border-white/15 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] font-mono-tech text-xs uppercase tracking-wider transition-colors"><Plus className="w-4 h-4" /> {t("admin.set_add_date")}</button>
        </div>
        {s.blocked_dates.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {s.blocked_dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#FF1A1A]/40 bg-[#FF1A1A]/10 text-[#FF1A1A] font-mono-tech text-xs" data-testid={`blocked-${d}`}>
                {d}<button onClick={() => rmBlocked(d)} className="hover:text-white"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("admin.set_save")}
      </button>
    </div>
  );
}
